import React, { useState, useEffect } from "react";
import {
  DEFAULT_MODULES, STATUS_MAP, newEntity, IDEA_SUBTYPES, newStudioNote,
  readAsDataUrl, downloadDataUrl, downloadText,
  IDEA_FIELD_CATALOG, resolveModuleFields, serializeModuleFields, defaultFieldsForModule,
} from "../../lib/gamecraft";
import { GlassCard, StatusDot, StatusSelect, TagInput, FileDropzone, ImageCropper } from "../gc/ui";
import { Plus, Trash, Download, Sparkle, PencilSimple, X, Copy, Play, Faders } from "@phosphor-icons/react";
import ModuleShell, { EmptyCraft, RelatedStrip } from "../gc/ModuleShell";
import { studioHealth, entityCraftScore, studioCast, matchesFocus } from "../../lib/studioHealth";
import { ai } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { useI18n } from "../../lib/i18n";

export default function IdeasPanel({ project, onChange, onJump, focus }) {
  const { lang } = useI18n();
  const { user } = useAuth();
  const modules = project.idea_modules?.length ? project.idea_modules : DEFAULT_MODULES;
  const entities = project.entities || {};
  const enabled = modules.filter((m) => m.enabled !== false);
  const [active, setActive] = useState(enabled[0]?.id || "characters");
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [newMod, setNewMod] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);

  useEffect(() => {
    if (!focus?.entityId) return;
    const ents = project.entities || {};
    Object.entries(ents).forEach(([mod, list]) => {
      if ((list || []).some((e) => e.id === focus.entityId)) {
        setActive(mod);
        setEditing(focus.entityId);
      }
    });
  }, [focus?.entityId]); // eslint-disable-line

  const list = entities[active] || [];
  const filtered = list.filter((e) => {
    const okStatus = filter === "all" || e.status === filter;
    const okQ = !q || `${e.name} ${e.role} ${(e.tags || []).join(" ")}`.toLowerCase().includes(q.toLowerCase());
    return okStatus && okQ;
  });

  const setModules = (next) => onChange({ idea_modules: next });
  const setList = (next) => onChange({ entities: { ...entities, [active]: next } });

  const addItem = () => {
    if (project.config?.scope_frozen) return toast.error("Scope freeze: no entran ideas nuevas");
    const item = newEntity({ name: lang === "es" ? "Nueva idea" : "New idea" });
    setList([...list, item]);
    setEditing(item.id);
  };

  const updateItem = (id, patch) => setList(list.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  const removeItem = (id) => setList(list.filter((e) => e.id !== id));

  const addModule = () => {
    if (project.config?.scope_frozen) return toast.error("Scope freeze: no entran módulos nuevos");
    const name = newMod.trim();
    if (!name) return;
    const id = name.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "") || `mod_${Date.now()}`;
    if (modules.some((m) => m.id === id)) return toast.error("Ese módulo ya existe");
    setModules([...modules, { id, name, enabled: true, color: "#6366F1", icon: "Cube", subcategories: [], fields: defaultFieldsForModule(id) }]);
    onChange({ entities: { ...entities, [id]: [] } });
    setNewMod("");
    setActive(id);
  };

  const removeModule = (id) => {
    if (!window.confirm("¿Quitar este módulo? Las ideas se conservan ocultas.")) return;
    setModules(modules.map((m) => (m.id === id ? { ...m, enabled: false } : m)));
    if (active === id) setActive(enabled.find((m) => m.id !== id)?.id || "characters");
  };

  const addSub = (name) => {
    const mod = modules.find((m) => m.id === active);
    const subs = [...(mod.subcategories || []), name];
    setModules(modules.map((m) => (m.id === active ? { ...m, subcategories: subs } : m)));
  };

  const suggest = async () => {
    setAiBusy(true);
    try {
      const mod = modules.find((m) => m.id === active);
      const res = await ai.ideas({
        prompt: `Sugiere 4 ideas concretas para el módulo "${mod?.name}" del juego 2D "${project.name}" (${project.genre}). Idea: ${project.gdd?.idea || "sin idea"}. Devuelve nombre + rol + 1 línea.`,
        category: active,
        language: lang,
        provider: user?.ai_provider || "auto",
      });
      toast.success("Sugerencias listas — revisa el chat de IA o pégalos como ideas.");
      const lines = (res.text || "").split("\n").filter((l) => l.trim().length > 3).slice(0, 4);
      const items = lines.map((l) => newEntity({ name: l.replace(/^[-*\d.\s]+/, "").slice(0, 48), notes: l, status: "thinking", tags: [active] }));
      setList([...list, ...items]);
    } catch {
      toast.error("No se pudo consultar la IA");
    } finally {
      setAiBusy(false);
    }
  };

  const exportJson = () => downloadText(JSON.stringify(list, null, 2), `${active}.json`, "application/json");

  const importJson = async (files) => {
    const text = await files[0].text();
    try {
      const parsed = JSON.parse(text);
      const arr = Array.isArray(parsed) ? parsed : [parsed];
      setList([...list, ...arr.map((x) => newEntity(x))]);
    } catch {
      toast.error("JSON inválido");
    }
  };

  const duplicate = (e) => {
    const copy = newEntity({ ...e, id: crypto.randomUUID(), name: `${e.name} copia` });
    setList([...list, copy]);
    setEditing(copy.id);
    toast.success("Copia lista para iterar");
  };

  const health = studioHealth(project);
  const ideaScore = health.modules.find((m) => m.id === "ideas")?.score ?? 0;
  const activeMod = modules.find((m) => m.id === active) || enabled[0];
  const subs = activeMod?.subcategories || [];
  const moduleFields = resolveModuleFields(activeMod);
  const current = list.find((e) => e.id === editing) || null;
  const cast = studioCast(project);

  const setModuleFields = (fields) => {
    const serialized = serializeModuleFields(fields);
    const next = modules.map((m) => (m.id === active ? { ...m, fields: serialized } : { ...m }));
    setModules(next);
  };

  return (
    <ModuleShell
      title="Ideas del proyecto"
      subtitle="Fichas con HP, tipo, sprite y notas. El motor lee héroe, mob y arma."
      beginner="Empieza por un héroe y un enemigo. HP y daño bastan para el playtest."
      expert="Alt+clic duplica. Enlaza sprite + código. Scope freeze bloquea altas nuevas."
      score={ideaScore}
      nextLabel="Probar ficha en motor"
      onNext={() => onJump?.("engine")}
      related={(
        <RelatedStrip items={cast.members.filter((m) => m.ent).map((m) => ({
          id: m.kind,
          label: `${m.role}: ${m.ent.name}`,
          img: m.spr?.data_url,
          onClick: () => { setActive(m.ent.kind || (m.kind === "weapon" ? "weapons" : m.kind === "enemy" ? "enemies" : "characters")); setEditing(m.ent.id); },
        }))} />
      )}
      actions={(
        <>
          <button className="gc-btn gc-btn-ghost h-10 px-3" onClick={suggest} disabled={aiBusy}><Sparkle size={14} /> {aiBusy ? "..." : "Sugerir con IA"}</button>
          <FileDropzone accept="application/json" onFiles={importJson} label="Importar JSON" />
          <button className="gc-btn gc-btn-ghost h-10 px-3" onClick={exportJson}><Download size={14} /> Exportar</button>
          <button className="gc-btn gc-btn-primary h-10 px-3" onClick={addItem}><Plus size={14} weight="bold" /> Nueva idea</button>
        </>
      )}
    >
      <div className="space-y-4">

      <GlassCard className="p-3">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {modules.filter((m) => m.enabled !== false).map((m) => (
            <button
              key={m.id}
              onClick={() => { setActive(m.id); setEditing(null); }}
              className="shrink-0 px-3 py-2 rounded-2xl text-left min-w-[120px] border transition"
              style={{
                borderColor: active === m.id ? m.color : "var(--gc-border)",
                background: active === m.id ? `${m.color}18` : "transparent",
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold">{m.name}</span>
                <span className="text-[10px] font-bold" style={{ color: m.color }}>{(entities[m.id] || []).length}</span>
              </div>
              <div className="flex gap-1 mt-1">
                {["approved", "thinking", "cut", "neutral"].map((s) => {
                  const n = (entities[m.id] || []).filter((e) => (e.status || "neutral") === s).length;
                  return n ? <StatusDot key={s} status={s} size={6} /> : null;
                })}
              </div>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-3 pt-3" style={{ borderTop: "1px solid var(--gc-border)" }}>
          <input className="gc-input h-9 px-3 text-sm flex-1 max-w-xs" placeholder="Nuevo módulo…" value={newMod} onChange={(e) => setNewMod(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addModule()} />
          <button className="gc-btn gc-btn-ghost h-9 px-3" onClick={addModule}><Plus size={14} /> Añadir módulo</button>
          <button className="gc-btn gc-btn-ghost h-9 px-3 text-red-500" onClick={() => removeModule(active)}><Trash size={14} /> Quitar actual</button>
        </div>
      </GlassCard>

      <div className="flex flex-wrap items-center gap-2">
        <input className="gc-input h-9 px-3 text-sm w-56" placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="gc-input h-9 px-3 text-sm" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">Todos los estados</option>
          {Object.values(STATUS_MAP).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <input
          className="gc-input h-9 px-3 text-sm w-48"
          placeholder="Nueva subcategoría…"
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.currentTarget.value.trim()) {
              addSub(e.currentTarget.value.trim());
              e.currentTarget.value = "";
            }
          }}
        />
        {subs.map((s) => <span key={s} className="gc-chip">{s}</span>)}
      </div>

      <div className="flex gap-3 items-start">
      <div className={"grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1 min-w-0 " + (current ? "xl:grid-cols-2" : "xl:grid-cols-3")}>
        {filtered.map((e) => {
          const sprite = (project.sprites || []).find((s) => s.id === e.linked_sprite_id);
          const craft = entityCraftScore(e);
          return (
            <GlassCard key={e.id} hover className={"p-0 overflow-hidden "
              + (editing === e.id ? "ring-2 ring-sky-400 " : "")
              + (matchesFocus(focus, { entityId: e.id, spriteId: e.linked_sprite_id }) ? "gc-focus-on " : "")
              + (cast.loopIds.has(e.linked_sprite_id) ? "gc-loop-on " : "")
            }>
              <div className="h-1.5" style={{ background: STATUS_MAP[e.status || "neutral"]?.color }} />
              <button type="button" className="p-4 w-full text-left" onClick={() => setEditing(e.id)}>
                <div className="flex items-start gap-3">
                  <div className="w-14 h-14 rounded-xl overflow-hidden flex items-center justify-center" style={{ background: "var(--gc-bg-accent)" }}>
                    {sprite ? (
                      <img src={sprite.data_url} alt="" className="w-full h-full object-contain" style={{ imageRendering: "pixelated" }} />
                    ) : (
                      <span className="text-xs" style={{ color: "var(--gc-faint)" }}>—</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{e.name}</div>
                    <div className="text-xs truncate" style={{ color: "var(--gc-muted)" }}>{cardSummary(e, moduleFields)}</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <span className="gc-chip text-[10px]">{craft}%</span>
                      {(e.tags || []).slice(0, 3).map((t) => <span key={t} className="gc-chip">#{t}</span>)}
                    </div>
                  </div>
                </div>
              </button>
              <div className="flex items-center justify-between px-4 pb-3">
                  <StatusSelect value={e.status || "neutral"} onChange={(s) => updateItem(e.id, { status: s })} />
                  <div className="flex gap-1">
                    {e.linked_sprite_id && (
                      <button className="gc-btn gc-btn-ghost h-8 px-2" title="Al motor" onClick={() => onJump?.("engine", { entityId: e.id, spriteId: e.linked_sprite_id })}><Play size={14} /></button>
                    )}
                    {e.linked_file_id && (
                      <button className="gc-btn gc-btn-ghost h-8 px-2 text-[10px]" onClick={() => onJump?.("code", { fileId: e.linked_file_id, entityId: e.id })}>JS</button>
                    )}
                    <button className="gc-btn gc-btn-ghost h-8 px-2" title="Duplicar" onClick={() => duplicate(e)}><Copy size={14} /></button>
                    <button className="gc-btn gc-btn-ghost h-8 px-2" onClick={() => setEditing(e.id)}><PencilSimple size={14} /></button>
                    <button className="gc-btn gc-btn-ghost h-8 px-2 text-red-500" onClick={() => removeItem(e.id)}><Trash size={14} /></button>
                  </div>
                </div>
            </GlassCard>
          );
        })}
        {filtered.length === 0 && (
          <div className="sm:col-span-2 xl:col-span-3">
          <EmptyCraft
            title="Este módulo está vacío"
            body="Una ficha concreta vale más que diez nombres. Héroe o slime, HP y un sprite."
            cta="Crear ficha"
            onCta={addItem}
            tips={["Sugerir con IA rellena 4 borradores", "Aprueba solo lo que vas a playtestear", "Liga sprite para verlo en el motor"]}
          />
          </div>
        )}
      </div>
      {current && (
        <aside className="hidden lg:block w-[min(100%,380px)] shrink-0 sticky top-0">
          <EntityEditor
            dock
            item={current}
            project={project}
            subs={subs}
            moduleId={active}
            moduleName={activeMod?.name || active}
            fields={moduleFields}
            onFieldsChange={setModuleFields}
            onJump={onJump}
            onClose={() => setEditing(null)}
            onChange={(patch) => updateItem(current.id, patch)}
            onCropSrc={setCropSrc}
            onProjectChange={onChange}
          />
        </aside>
      )}
      </div>

      {current && (
        <div className="lg:hidden">
        <EntityEditor
          item={current}
          project={project}
          subs={subs}
          moduleId={active}
          moduleName={activeMod?.name || active}
          fields={moduleFields}
          onFieldsChange={setModuleFields}
          onJump={onJump}
          onClose={() => setEditing(null)}
          onChange={(patch) => updateItem(current.id, patch)}
          onCropSrc={setCropSrc}
          onProjectChange={onChange}
        />
        </div>
      )}
      {cropSrc && (
        <ImageCropper
          src={cropSrc.src}
          onClose={() => setCropSrc(null)}
          onCrop={(out) => {
            const atts = [...(current?.attachments || []), { id: crypto.randomUUID(), name: "recorte.png", type: "image", data_url: out }];
            updateItem(current.id, { attachments: atts });
            setCropSrc(null);
          }}
        />
      )}
    </div>
    </ModuleShell>
  );
}

function cardSummary(e, fields) {
  const bits = [];
  const has = (id) => fields.some((f) => f.id === id);
  if (has("role") && e.role) bits.push(e.role);
  if (has("subtype") && e.subtype) bits.push(e.subtype);
  if (has("hp")) bits.push(`HP ${e.hp ?? 0}`);
  if (has("damage")) bits.push(`DMG ${e.damage ?? 0}`);
  if (has("speed") && !has("hp")) bits.push(`VEL ${e.speed ?? 0}`);
  return bits.join(" · ") || "Sin datos";
}

function fieldValue(item, field) {
  if (field.custom) return item.attrs?.[field.id] ?? (field.type === "number" ? 0 : "");
  if (field.type === "number") return item[field.id] ?? 0;
  return item[field.id] ?? "";
}

function fieldPatch(item, field, value) {
  if (field.custom) return { attrs: { ...(item.attrs || {}), [field.id]: value } };
  return { [field.id]: value };
}

function EntityEditor({
  item, project, subs, moduleId, moduleName, fields, onFieldsChange,
  onChange, onClose, onJump, onCropSrc, onProjectChange, dock,
}) {
  const sprites = project.sprites || [];
  const files = project.files || [];
  const samples = project.samples || [];
  const [schemaOpen, setSchemaOpen] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newType, setNewType] = useState("text");

  const used = new Set((fields || []).map((f) => f.id));
  const unusedCatalog = IDEA_FIELD_CATALOG.filter((f) => !used.has(f.id));

  const attach = async (filesList) => {
    const atts = [...(item.attachments || [])];
    for (const f of filesList) {
      const data_url = await readAsDataUrl(f);
      atts.push({ id: crypto.randomUUID(), name: f.name, type: f.type, data_url });
    }
    onChange({ attachments: atts });
  };

  const addCatalogField = (id) => {
    const cat = IDEA_FIELD_CATALOG.find((f) => f.id === id);
    if (!cat) return;
    onFieldsChange([...(fields || []), { ...cat }]);
    toast.success(`Campo “${cat.label}” en ${moduleName}`);
  };

  const addCustomField = () => {
    const label = newLabel.trim();
    if (!label) return;
    const id = `f_${crypto.randomUUID().slice(0, 8)}`;
    onFieldsChange([...(fields || []), { id, label, type: newType, custom: true }]);
    setNewLabel("");
    toast.success(`Campo “${label}” en todas las fichas de ${moduleName}`);
  };

  const removeField = (id) => {
    const f = fields.find((x) => x.id === id);
    if (f?.locked) return;
    onFieldsChange(fields.filter((x) => x.id !== id));
  };

  const renameField = (id, label) => {
    onFieldsChange(fields.map((f) => (f.id === id ? { ...f, label } : f)));
  };

  const renderField = (field) => {
    const val = fieldValue(item, field);
    const set = (v) => onChange(fieldPatch(item, field, v));
    const wrap = field.wide ? "md:col-span-2" : "";
    if (field.type === "subtype") {
      return (
        <Field key={field.id} label={field.label} className={wrap}>
          <select className="gc-input h-10 px-3 w-full" value={item.subtype || ""} onChange={(e) => onChange({ subtype: e.target.value })}>
            <option value="">—</option>
            {(IDEA_SUBTYPES[moduleId] || []).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </Field>
      );
    }
    if (field.type === "subcategory") {
      return (
        <Field key={field.id} label={field.label} className={wrap}>
          <select className="gc-input h-10 px-3 w-full" value={item.subcategory || ""} onChange={(e) => onChange({ subcategory: e.target.value })}>
            <option value="">—</option>
            {subs.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      );
    }
    if (field.type === "status") {
      return (
        <Field key={field.id} label={field.label} className={wrap}>
          <StatusSelect value={item.status || "neutral"} onChange={(s) => onChange({ status: s })} />
        </Field>
      );
    }
    if (field.type === "tags") {
      return (
        <Field key={field.id} label={field.label} className={wrap}>
          <TagInput tags={item.tags || []} onChange={(tags) => onChange({ tags })} />
        </Field>
      );
    }
    if (field.type === "textarea") {
      return (
        <Field key={field.id} label={field.label} className={wrap}>
          <textarea className="gc-input w-full p-3 text-sm min-h-[90px]" value={val || ""} onChange={(e) => set(e.target.value)} />
        </Field>
      );
    }
    if (field.type === "number") {
      return (
        <Field key={field.id} label={field.label} className={wrap}>
          <input type="number" className="gc-input h-10 px-3 w-full" value={val} onChange={(e) => set(+e.target.value)} />
        </Field>
      );
    }
    return (
      <Field key={field.id} label={field.label} className={wrap}>
        <input className="gc-input h-10 px-3 w-full" value={val || ""} onChange={(e) => set(e.target.value)} />
      </Field>
    );
  };

  const inner = (
      <div className={"gc-card overflow-auto p-5 " + (dock ? "max-h-[calc(100vh-220px)]" : "w-full max-w-2xl max-h-[90vh]")}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-lg">Editar idea</h3>
          <div className="flex items-center gap-1">
            <button
              type="button"
              className={"gc-btn gc-btn-ghost h-8 px-2 text-xs " + (schemaOpen ? "gc-chip-on" : "")}
              onClick={() => setSchemaOpen((v) => !v)}
              title={`Campos de ${moduleName}`}
            >
              <Faders size={14} /> Campos
            </button>
            <button type="button" onClick={onClose}><X size={18} /></button>
          </div>
        </div>
        <p className="text-[11px] mb-3" style={{ color: "var(--gc-muted)" }}>
          Ficha de <span className="font-semibold">{moduleName}</span>. Los campos valen para todas las ideas de este módulo.
        </p>

        {schemaOpen && (
          <div className="mb-4 p-3 rounded-2xl space-y-3" style={{ background: "var(--gc-surface-soft)", border: "1px solid var(--gc-border)" }}>
            <div className="text-[11px] font-semibold" style={{ color: "var(--gc-muted)" }}>Esquema de {moduleName}</div>
            <div className="space-y-1.5">
              {fields.map((f) => (
                <div key={f.id} className="flex items-center gap-2">
                  <input
                    className="gc-input h-8 px-2 text-xs flex-1"
                    value={f.label}
                    onChange={(e) => renameField(f.id, e.target.value)}
                  />
                  <span className="text-[10px] w-16 shrink-0" style={{ color: "var(--gc-faint)" }}>{f.type}</span>
                  {f.locked ? (
                    <span className="text-[10px] w-8 text-center" style={{ color: "var(--gc-faint)" }}>—</span>
                  ) : (
                    <button type="button" className="text-red-500 w-8" title="Quitar del módulo" onClick={() => removeField(f.id)}>
                      <Trash size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {unusedCatalog.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {unusedCatalog.map((f) => (
                  <button key={f.id} type="button" className="gc-chip" onClick={() => addCatalogField(f.id)}>
                    + {f.label}
                  </button>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <input
                className="gc-input h-8 px-2 text-xs flex-1 min-w-[120px]"
                placeholder="Campo nuevo (ej. Rango)"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomField())}
              />
              <select className="gc-input h-8 px-2 text-xs" value={newType} onChange={(e) => setNewType(e.target.value)}>
                <option value="text">Texto</option>
                <option value="number">Número</option>
                <option value="textarea">Texto largo</option>
              </select>
              <button type="button" className="gc-btn gc-btn-ghost h-8 px-2 text-xs" onClick={addCustomField}>
                <Plus size={12} /> Añadir
              </button>
            </div>
            <div className="text-[10px]" style={{ color: "var(--gc-faint)" }}>
              Añadir o quitar cambia todas las fichas de {moduleName}. Los valores ya escritos no se borran.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {fields.map(renderField)}
        </div>
        <button
          type="button"
          className="gc-btn gc-btn-ghost h-10 px-3 mt-3 text-xs"
          onClick={() => {
            const note = newStudioNote({
              title: item.name || "Nota de idea",
              body: item.notes || "",
              category: "idea",
              links: [
                { type: "entity", id: item.id, label: item.name },
                { type: "module", id: moduleId, label: moduleId },
                item.subtype ? { type: "class", id: item.subtype, label: item.subtype } : null,
                item.subcategory ? { type: "class", id: item.subcategory, label: item.subcategory } : null,
              ].filter(Boolean),
            });
            onProjectChange({ notes: [note, ...(project.notes || [])] });
            toast.success("Nota ligada a esta idea");
          }}
        >
          Guardar como nota del estudio
        </button>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <Field label="Sprite">
            <select className="gc-input h-10 px-3 w-full" value={item.linked_sprite_id || ""} onChange={(e) => onChange({ linked_sprite_id: e.target.value })}>
              <option value="">—</option>
              {sprites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Código">
            <select className="gc-input h-10 px-3 w-full" value={item.linked_file_id || ""} onChange={(e) => onChange({ linked_file_id: e.target.value })}>
              <option value="">—</option>
              {files.map((f) => <option key={f.id} value={f.id}>{f.path}</option>)}
            </select>
          </Field>
          <Field label="Audio">
            <select className="gc-input h-10 px-3 w-full" value={item.linked_audio_id || ""} onChange={(e) => onChange({ linked_audio_id: e.target.value })}>
              <option value="">—</option>
              {samples.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <FileDropzone accept="image/*,audio/*,.js,.py,.gd,.cs,.json" multiple onFiles={attach} label="Adjuntar archivo" />
          {item.linked_sprite_id && <button className="gc-btn gc-btn-ghost h-9 px-3 text-xs" onClick={() => onJump?.("sprites")}>Abrir sprite</button>}
          {item.linked_file_id && <button className="gc-btn gc-btn-ghost h-9 px-3 text-xs" onClick={() => onJump?.("code")}>Abrir código</button>}
          <button
            className="gc-btn gc-btn-primary h-9 px-3 text-xs"
            onClick={() => {
              const playtest = { ...(project.config?.playtest || {}) };
              if (item.linked_sprite_id) {
                const spr = sprites.find((s) => s.id === item.linked_sprite_id);
                const isGear = spr?.studio === "gear" || /weapon|arma|item|potion/i.test(`${spr?.category || ""} ${item.subtype || ""} ${moduleId}`);
                playtest[isGear ? "weaponId" : "creatureId"] = item.linked_sprite_id;
              }
              onProjectChange?.({ config: { ...(project.config || {}), playtest } });
              onJump?.("engine");
            }}
          >
            Probar en el motor
          </button>
        </div>
        {(item.attachments || []).length > 0 && (
          <div className="mt-3 space-y-1">
            {item.attachments.map((a) => (
              <div key={a.id} className="flex items-center gap-2 text-xs">
                <span className="truncate flex-1">{a.name}</span>
                {a.type?.startsWith("image") && (
                  <button className="gc-chip" onClick={() => onCropSrc({ src: a.data_url })}>Recortar</button>
                )}
                <button className="gc-chip" onClick={() => downloadDataUrl(a.data_url, a.name)}>Descargar</button>
                <button className="text-red-500" onClick={() => onChange({ attachments: item.attachments.filter((x) => x.id !== a.id) })}>✕</button>
              </div>
            ))}
          </div>
        )}
      </div>
  );
  if (dock) return inner;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-3">
      {inner}
    </div>
  );
}

function Field({ label, children, className }) {
  return (
    <label className={className}>
      <div className="text-[11px] font-semibold mb-1" style={{ color: "var(--gc-muted)" }}>{label}</div>
      {children}
    </label>
  );
}
